import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { type CfAccountInfo } from '@chainflip/rpc/types';
import { assert, unreachable } from '@chainflip/utils/assertion';
import { type ApiPromise } from '@polkadot/api';
import {
  type QueryObserverResult,
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useWalletClient as useEvmWalletClient } from 'wagmi';
import useAccountInfo, { type AccountInfoWithId } from '@/shared/hooks/useAccountInfo';
import {
  type ChainflipAccountId,
  type InjectedAccountWithMeta,
  type ReadonlyAccount,
  usePolkadot,
} from '@/shared/hooks/usePolkadot';
import usePrevious from '@/shared/hooks/usePrevious';
import useToast, { type DismissableToast } from '@/shared/hooks/useToast';
import EvmExtrinsicSDK from '@/shared/utils/EvmExtrinsicSDK';
import type ExtrinsicSDK from '@/shared/utils/ExtrinsicSDK';
import PolkadotExtrinsicSDK from '@/shared/utils/PolkadotExtrinsicSDK';
import { initRpcWsConnection } from '@/shared/utils/rpc';
import { formatRuntimeVersion } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';

type RequireKeys<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};

type AccountWithRole<T extends AccountRole> = Extract<CfAccountInfo, { role: T }>;

const isAccountWithRole = <T extends AccountRole>(
  account: CfAccountInfo | null,
  role: T,
): account is AccountWithRole<T> => {
  if (account === null) return false;

  return account.role === role;
};

export type Context<T, R extends AccountRole, A extends Record<string, unknown>> = T & {
  account: AccountWithRole<R> | null;
  accountInfo: CfAccountInfo | null;
  accountId: ChainflipAccountId | null;
  isLoading: boolean;
  flipBalance: TokenAmount | null;
  redeemableBalance: TokenAmount | null;
  AccountSuspense: (props: {
    fallback: React.JSX.Element;
    children: (context: OnboardedContext<T, R, A> & { fallback: React.JSX.Element }) => ReactNode;
  }) => React.JSX.Element | null;
  runtimeVersion: string | null;
  refreshAccountInfo: () => Promise<QueryObserverResult<AccountInfoWithId, Error>>;
  apiPromise: ApiPromise | null;
  additionalInfo: A;
};

export type AccountRole = CfAccountInfo['role'];

export type OnboardedContext<T, R extends AccountRole, A extends Record<string, unknown>> = Omit<
  RequireKeys<Context<T, R, A>, 'account' | 'accountId' | keyof T>,
  'account'
> & { account: AccountWithRole<R> };

type CreateContextOpts<T, R extends AccountRole, A> = {
  parseAccountInfo: (account: CfAccountInfo | null, additionalInfo: A) => T;
  polkadotAppName: string;
  onboardedRole: R;
  useAdditionalInfo?: (
    account: InjectedAccountWithMeta | ReadonlyAccount | null,
    apiPromise: ApiPromise | null,
  ) => A;
};

type ExtrinsicArgs<E extends keyof typeof ExtrinsicSDK.prototype> = Parameters<
  (typeof ExtrinsicSDK.prototype)[E]
>;

export type ExtrinsicResult<E extends keyof typeof ExtrinsicSDK.prototype> = Awaited<
  ReturnType<(typeof ExtrinsicSDK.prototype)[E]>
>;

export type ExtrinsicSDKContext<T = unknown> = T & {
  toast: DismissableToast;
  getExtrinsicSDK: () => ExtrinsicSDK;
};

export type UseExtrinsicMutationResult<E extends keyof typeof ExtrinsicSDK.prototype> = Omit<
  UseMutationResult<ExtrinsicResult<E>, Error, ExtrinsicArgs<E>>,
  'mutate'
> & {
  mutate: (...args: ExtrinsicArgs<E>) => void;
};

export function createStateChainAccountContext<
  T,
  R extends AccountRole,
  A extends Record<string, unknown> = Record<string, unknown>,
>({
  parseAccountInfo,
  onboardedRole,
  useAdditionalInfo = () => null as unknown as A,
}: CreateContextOpts<T, R, A>) {
  const StateChainAccountContext = createContext<Context<T, R, A> | null>(null);

  function StateChainAccountProvider({ children }: { children: ReactNode }) {
    const { data: apiPromise = null } = useQuery({
      queryKey: ['apiPromise'],
      queryFn: initRpcWsConnection,
      retry: true,
    });

    const isLoading = !apiPromise;
    const { selectedAccount: polkadotAccount, selectAccount } = usePolkadot();

    const runtimeVersion = useMemo(() => {
      if (apiPromise === null) return null;

      return formatRuntimeVersion(apiPromise.runtimeVersion.specVersion.toNumber());
    }, [apiPromise]);

    const { data: account = null, refetch } = useAccountInfo(polkadotAccount?.idSs58, {
      refetchInterval: 5000,
    });

    useEffect(() => {
      if (account?.role === 'liquidity_provider' && polkadotAccount?.type === 'evm') {
        selectAccount({ ...polkadotAccount, onboarded: true });
      }
    }, [account?.role, polkadotAccount?.type]);

    const additionalInfo = useAdditionalInfo(polkadotAccount, apiPromise);

    // flatten all chain balances for easy retrieval
    const accountInfo = useMemo(
      () => parseAccountInfo(account, additionalInfo),
      [account, additionalInfo],
    );

    const prevAccountInfo = usePrevious(account);
    const toast = useToast('evmAccountOnboarded');

    useEffect(() => {
      if (prevAccountInfo?.role === 'unregistered' && account?.role === 'liquidity_provider') {
        // account just onboarded
        toast.success('Deposit complete! Your liquidity provider account is now active.');
      }
    }, [account?.role]);

    const memoizedContext = useMemo(
      () => ({
        account: isAccountWithRole(account, onboardedRole) ? account : null,
        accountInfo: account,
        isLoading,
        accountId: polkadotAccount?.idSs58 ?? null,
        flipBalance: TokenAmount.fromAsset(account?.flip_balance, 'Flip'),
        redeemableBalance: TokenAmount.fromAsset(account?.estimated_redeemable_balance, 'Flip'),
        ...accountInfo,
        AccountSuspense: null as unknown as Context<T, R, A>['AccountSuspense'],
        runtimeVersion,
        refreshAccountInfo: refetch,
        apiPromise,
        additionalInfo,
      }),
      [account, isLoading, accountInfo, polkadotAccount?.idSs58, apiPromise, additionalInfo],
    );

    return (
      <StateChainAccountContext.Provider value={memoizedContext}>
        {children}
      </StateChainAccountContext.Provider>
    );
  }

  function useStateChainAccount(): Context<T, R, A> | OnboardedContext<T, R, A> {
    const ctx = useContext(StateChainAccountContext);
    if (ctx === null) {
      throw new Error('useStateChainAccount must be used within a StateChainAccountContext');
    }

    const AccountSuspense = (({ fallback, children }) => {
      if (fallback && ctx!.account?.role !== onboardedRole) return fallback;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return children({ ...ctx, fallback } as any);
    }) as Context<T, R, A>['AccountSuspense'];

    return { ...ctx, AccountSuspense };
  }

  function useExtrinsicSDK(): ExtrinsicSDKContext;
  function useExtrinsicSDK<E extends keyof typeof ExtrinsicSDK.prototype>(
    extrinsic: E,
    toastName?: string,
  ): ExtrinsicSDKContext<{
    sendExtrinsic: (...args: ExtrinsicArgs<E>) => Promise<ExtrinsicResult<E>>;
  }>;
  function useExtrinsicSDK<E extends keyof typeof ExtrinsicSDK.prototype>(
    extrinsic?: E,
    toastName?: string,
  ) {
    const queryClient = useQueryClient();
    const toast = useToast(toastName ?? extrinsic ?? 'sdk');

    const {
      selectedAccount: polkadotAccount,
      selectedSigner: polkadotSigner,
      isInitializingExtensions,
    } = usePolkadot();
    const { data: evmWalletClient } = useEvmWalletClient();

    const { accountInfo, refreshAccountInfo, apiPromise } = useStateChainAccount() as Context<
      T,
      R,
      A
    >;

    const init = useCallback(() => {
      assert(
        !isInitializingExtensions || polkadotAccount?.type === 'evm',
        "We couldn't reach your wallet. Please check there's no pending requests from other wallet extensions.",
      );
      assert(polkadotAccount, 'wallet account not initialized');
      assert(!polkadotAccount.readonly, 'wallet account is readonly');
      assert(
        apiPromise,
        'Unable to initialize a Websocket connection to the Chainflip RPC endpoint. Please make sure that you are connected to the internet.',
      );
      assert(accountInfo, 'no account info');
      if (polkadotAccount.type === 'substrate') {
        assert(
          polkadotSigner,
          "We couldn't reach your wallet. Please check there's no pending connection requests in your wallet extension.",
        );
        return new PolkadotExtrinsicSDK(
          accountInfo,
          apiPromise,
          polkadotAccount.idSs58,
          refreshAccountInfo,
          queryClient,
          polkadotSigner,
        );
      }

      if (polkadotAccount.type === 'evm') {
        assert(evmWalletClient, 'no wallet client for connected evm account');
        if (
          evmWalletClient.account.address.toLowerCase() !== polkadotAccount.evmAddress.toLowerCase()
        ) {
          throw new Error(
            `Your connected EVM wallet address does not match the selected Chainflip account. Please change your address in ${polkadotAccount.meta.name || 'your wallet extension'}.`,
          );
        }
        return new EvmExtrinsicSDK(
          accountInfo,
          apiPromise,
          polkadotAccount.idSs58,
          refreshAccountInfo,
          queryClient,
          evmWalletClient,
        );
      }

      return unreachable(polkadotAccount, 'unexpected account type');
    }, [
      polkadotAccount,
      polkadotSigner,
      isInitializingExtensions,
      accountInfo,
      refreshAccountInfo,
      apiPromise,
      evmWalletClient,
    ]);

    const sendExtrinsic = useCallback(
      async (...args: ExtrinsicArgs<E>): Promise<ExtrinsicResult<E>> => {
        if (extrinsic === undefined) throw new Error('no extrinsic provided');
        const sdk = init();
        return sdk[extrinsic](
          // @ts-expect-error -- no idea
          ...args,
        ) as ExtrinsicResult<E>;
      },
      [init],
    );

    return { sendExtrinsic, toast, getExtrinsicSDK: init };
  }

  function useExtrinsicMutation<E extends keyof typeof ExtrinsicSDK.prototype>(
    name: E,
    opts: Exclude<UseMutationOptions<ExtrinsicResult<E>, Error, ExtrinsicArgs<E>>, 'mutateionFn'>,
  ): UseExtrinsicMutationResult<E> {
    const { sendExtrinsic } = useExtrinsicSDK(name);

    const mutation = useMutation({
      ...opts,
      mutationFn: (args: ExtrinsicArgs<E>) => sendExtrinsic(...args),
    });

    return {
      ...mutation,
      mutate: useCallback((...args: ExtrinsicArgs<E>) => mutation.mutate(args), [mutation.mutate]),
    };
  }

  return { StateChainAccountProvider, useStateChainAccount, useExtrinsicSDK, useExtrinsicMutation };
}

export interface UseStateChainAccount<
  T,
  R extends AccountRole,
  A extends Record<string, unknown> = Record<string, unknown>,
> {
  (): Context<T, R, A> | OnboardedContext<T, R, A>;
}

export type DepositChannel = ExtrinsicResult<'openChannel'>;
